import { apiClient } from './apiClient';
import { fileService } from './fileService';
import { debugLogger } from '../utils/debugLogger';
import { Badge } from '../types/badge';

// User Profile Response Interface
export interface UserProfile {
  id: number;
  username: string;
  email: string;
  phone?: string;
  displayName: string;
  avatarUrl?: string | null;
  bio?: string | null;
  gender?: string | null;
  birthday?: string | null;
  location?: string | null;
  isActive: boolean;
  badges: Badge[];
}

// Update Profile Request Interface
export interface UpdateProfileRequest {
  displayName?: string;
  phone?: string;
  bio?: string | null;
  gender?: 'MALE' | 'FEMALE' | 'OTHER';
  birthday?: string | null;
  location?: string | null;
  avatarUrl?: string | null;
  coverUrl?: string | null;
}

// Change Password Request Interface
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// Upload Avatar Response Interface
export interface UploadAvatarResponse {
  result: 'SUCCESS' | 'ERROR';
  message: string;
  data: string | null;
}

class UserService {
  private baseUrl = '/api/users';

  // Get current user profile
  async getProfile(): Promise<UserProfile> {
    const endpoint = `${this.baseUrl}/profile`;
    debugLogger.logApiCall('GET', endpoint);
    try {
      const response = await apiClient.get<UserProfile>(endpoint);
      debugLogger.logApiResponse('GET', endpoint, response);
      
      // Ensure badges is always an array (never null or undefined)
      const normalizedResponse: UserProfile = {
        ...response,
        badges: response.badges || []
      };
      
      debugLogger.log('UserService', `👤 Get Profile API Success`, {
        endpoint,
        userId: normalizedResponse.id,
        username: normalizedResponse.username,
        displayName: normalizedResponse.displayName,
        email: normalizedResponse.email,
        phone: normalizedResponse.phone,
        bio: normalizedResponse.bio?.substring(0, 50) + '...',
        gender: normalizedResponse.gender,
        birthday: normalizedResponse.birthday,
        location: normalizedResponse.location,
        avatarUrl: normalizedResponse.avatarUrl,
        isActive: normalizedResponse.isActive,
        badgesCount: normalizedResponse.badges.length
      });
      
      return normalizedResponse;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      debugLogger.log('UserService', `❌ Get Profile API Error`, {
        endpoint,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  // Get user by ID
  async getUserById(id: number): Promise<UserProfile> {
    const endpoint = `${this.baseUrl}/${id}`;
    debugLogger.logApiCall('GET', endpoint, { userId: id });
    try {
      const response = await apiClient.get<UserProfile>(endpoint);
      debugLogger.logApiResponse('GET', endpoint, response);
      
      // Ensure badges is always an array (never null or undefined)
      const normalizedResponse: UserProfile = {
        ...response,
        badges: response.badges || []
      };
      
      debugLogger.log('UserService', `👤 Get User by ID API Success`, {
        endpoint,
        userId: normalizedResponse.id,
        username: normalizedResponse.username,
        badgesCount: normalizedResponse.badges.length
      });
      
      return normalizedResponse;
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      throw error;
    }
  }

  async updateProfile(data: UpdateProfileRequest): Promise<UserProfile> {
    const endpoint = `${this.baseUrl}/profile`;
    debugLogger.logApiCall('PUT', endpoint, data);
    try {
      const payload: any = {}
      if (data.displayName !== undefined) payload.displayName = data.displayName
      if (data.phone !== undefined) payload.phone = data.phone
      if (data.bio !== undefined) payload.bio = data.bio
      if (data.gender !== undefined && typeof data.gender === 'string') {
        payload.gender = data.gender.toLowerCase();
      }
      if (data.birthday !== undefined) payload.birthday = data.birthday
      if (data.location !== undefined) payload.location = data.location
      if (data.avatarUrl !== undefined) payload.avatarUrl = data.avatarUrl
      if (data.coverUrl !== undefined) payload.coverUrl = data.coverUrl

      const response = await apiClient.put<UserProfile>(endpoint, payload);
      debugLogger.logApiResponse('PUT', endpoint, response);
      
      // Ensure badges is always an array (never null or undefined)
      const normalizedResponse: UserProfile = {
        ...response,
        badges: response.badges || []
      };
      
      debugLogger.log('UserService', 'Profile updated successfully', {
        updatedFields: Object.keys(payload),
        badgesCount: normalizedResponse.badges.length
      });
      return normalizedResponse;
    } catch (error) {
      debugLogger.logApiResponse('PUT', endpoint, null, error);
      throw error;
    }
  }

  // Change password
  async changePassword(data: ChangePasswordRequest): Promise<string> {
    const endpoint = `${this.baseUrl}/change-password`;
    debugLogger.logApiCall('PUT', endpoint, { hasCurrentPassword: !!data.currentPassword, hasNewPassword: !!data.newPassword });
    try {
      const response = await apiClient.put<string>(endpoint, data);
      debugLogger.logApiResponse('PUT', endpoint, response);
      debugLogger.log('UserService', 'Password changed successfully');
      return response;
    } catch (error) {
      debugLogger.logApiResponse('PUT', endpoint, null, error);
      throw error;
    }
  }

  // Upload avatar using file service
  async uploadAvatar(_file: File): Promise<UploadAvatarResponse> {
    throw new Error('upload-avatar endpoint was removed. Upload to storage and send avatarUrl in PUT /api/users/profile instead.')
  }

  // Upload avatar using general file service (alternative method)
  async uploadAvatarViaFileService(_file: File): Promise<UploadAvatarResponse> {
    throw new Error('File upload service was removed. Use storageService to upload and send avatarUrl in profile update.')
  }

  // Deactivate account
  async deactivateAccount(): Promise<string> {
    const endpoint = `${this.baseUrl}/deactivate`;
    debugLogger.logApiCall('PUT', endpoint);
    try {
      const response = await apiClient.put<string>(endpoint);
      debugLogger.logApiResponse('PUT', endpoint, response);
      debugLogger.log('UserService', 'Account deactivated successfully');
      return response;
    } catch (error) {
      debugLogger.logApiResponse('PUT', endpoint, null, error);
      throw error;
    }
  }

  // Activate account
  async activateAccount(): Promise<string> {
    const endpoint = `${this.baseUrl}/activate`;
    debugLogger.logApiCall('PUT', endpoint);
    try {
      const response = await apiClient.put<string>(endpoint);
      debugLogger.logApiResponse('PUT', endpoint, response);
      debugLogger.log('UserService', 'Account activated successfully');
      return response;
    } catch (error) {
      debugLogger.logApiResponse('PUT', endpoint, null, error);
      throw error;
    }
  }

  // Check username availability
  async checkUsernameAvailability(username: string): Promise<boolean> {
    const endpoint = `${this.baseUrl}/check-username/${username}`;
    debugLogger.logApiCall('GET', endpoint, { username });
    try {
      const response = await apiClient.get<boolean>(endpoint);
      debugLogger.logApiResponse('GET', endpoint, response);
      debugLogger.log('UserService', 'Username availability checked', {
        username,
        isAvailable: !response // API returns true if exists, we want availability
      });
      return !response; // API returns true if username exists, we return availability
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      throw error;
    }
  }

  // Check email availability
  async checkEmailAvailability(email: string): Promise<boolean> {
    const endpoint = `${this.baseUrl}/check-email/${email}`;
    debugLogger.logApiCall('GET', endpoint, { email });
    try {
      const response = await apiClient.get<boolean>(endpoint);
      debugLogger.logApiResponse('GET', endpoint, response);
      debugLogger.log('UserService', 'Email availability checked', {
        email,
        isAvailable: !response // API returns true if exists, we want availability
      });
      return !response; // API returns true if email exists, we return availability
    } catch (error) {
      debugLogger.logApiResponse('GET', endpoint, null, error);
      throw error;
    }
  }
}

// Export service instance and legacy functions for backward compatibility
export const userService = new UserService();

// Legacy exports for backward compatibility
export const getProfile = () => userService.getProfile();
export const getUserById = (id: string) => userService.getUserById(parseInt(id));
export const updateProfile = (data: any) => userService.updateProfile(data);
export const changePassword = (data: ChangePasswordRequest) => userService.changePassword(data);
export const deactivateAccount = () => userService.deactivateAccount();
export const activateAccount = () => userService.activateAccount();
export const checkUsernameExists = (username: string) => userService.checkUsernameAvailability(username).then(available => !available);
export const checkEmailExists = (email: string) => userService.checkEmailAvailability(email).then(available => !available);